/**
 * The screen shown inside a settings item, carried by the URL next to `?settings=<item>`:
 *
 *   (nothing)               the item's list (or its only page)
 *   `view=new`              the create form
 *   `view=edit&id=<id>`     the edit form of one record
 *
 * Reloading or sharing the link opens the same screen. Any other value is the list.
 */
export type SettingsScreen = { view: 'list' } | { view: 'new' } | { view: 'edit'; id: string };

/** Search param of the screen (`new` | `edit`). */
export const SETTINGS_VIEW_PARAM = 'view';
/** Search param of the record an `edit` screen opens. */
export const SETTINGS_ID_PARAM = 'id';

export const SETTINGS_LIST_SCREEN: SettingsScreen = { view: 'list' };

/** The search params of a screen, as they go in the URL (none for the list). */
export interface SettingsScreenSearch {
  view?: 'new' | 'edit';
  id?: string;
}

/** Reads the screen from the search params; anything unexpected is the list. */
export function parseSettingsScreen(search: Record<string, unknown>): SettingsScreen {
  const view = search[SETTINGS_VIEW_PARAM];
  if (view === 'new') return { view: 'new' };
  const id = search[SETTINGS_ID_PARAM];
  if (view === 'edit' && typeof id === 'string' && id.trim() !== '') return { view: 'edit', id };
  return SETTINGS_LIST_SCREEN;
}

/** The search params that put `screen` in the URL. */
export function settingsScreenSearch(screen: SettingsScreen): SettingsScreenSearch {
  switch (screen.view) {
    case 'new':
      return { view: 'new' };
    case 'edit':
      return { view: 'edit', id: screen.id };
    case 'list':
      return {};
  }
}

/** `search` with the screen params replaced by those of `screen`. */
export function withSettingsScreen(
  search: Record<string, unknown>,
  screen: SettingsScreen,
): Record<string, unknown> {
  return { ...withoutSettingsScreen(search), ...settingsScreenSearch(screen) };
}

/** `search` without the screen params. */
export function withoutSettingsScreen(search: Record<string, unknown>): Record<string, unknown> {
  const { [SETTINGS_VIEW_PARAM]: _view, [SETTINGS_ID_PARAM]: _id, ...rest } = search;
  return rest;
}

/** Whether two screens are the same one (same view and record). */
export function sameSettingsScreen(a: SettingsScreen, b: SettingsScreen): boolean {
  if (a.view !== b.view) return false;
  return a.view !== 'edit' || (b.view === 'edit' && a.id === b.id);
}
